import { useRef } from "react";
import PropTypes from "prop-types";
import { Drawer, Divider } from "@mantine/core";
import { HiXMark, HiPrinter, HiUser } from "react-icons/hi2";
import { useReactToPrint } from "react-to-print";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import { BULK_UPLOAD_STATUS_MAP } from "../../constants/bulkUploadsConstants";
import {
  formatKES,
  formatDateTimeToLocale,
  getDisplayValue,
  getInitials,
  EMPTY_FIELD_PLACEHOLDER,
} from "../../../../utils/transactionutils";

const CHANNEL_LABEL = {
  Mpesa: "M-Pesa",
  Bank: "Bank",
};

function RecipientAvatar({ name }) {
  const initials = getInitials(name);
  return (
    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
      {initials ? (
        <span className="text-sm font-bold text-primary-700">{initials}</span>
      ) : (
        <HiUser size={20} className="text-primary-700" />
      )}
    </div>
  );
}

RecipientAvatar.propTypes = {
  name: PropTypes.string,
};

function ReceiptFieldRow({ label, value, labelClassName, valueClassName }) {
  return (
    <div>
      <div className="flex items-center justify-between py-3 gap-4">
        <span
          className={`text-sm shrink-0 ${labelClassName ?? "text-gray-400"}`}
        >
          {label}
        </span>
        <span
          className={`text-sm text-right ${valueClassName ?? "font-semibold text-gray-900"}`}
        >
          {value}
        </span>
      </div>
      <Divider color="gray.2" />
    </div>
  );
}

ReceiptFieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  labelClassName: PropTypes.string,
  valueClassName: PropTypes.string,
};

function ReceiptContent({ row }) {
  const total = row == null ? null : (row.amount ?? 0) + (row.charges ?? 0);
  const isMpesa = row?.paymentChannel === "Mpesa";
  const isBank = row?.paymentChannel === "Bank";

  const headerSubtitle = isBank
    ? getDisplayValue(row?.accountNumber)
    : getDisplayValue(row?.phoneNumber);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <RecipientAvatar name={row?.recipientName} />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {getDisplayValue(row?.recipientName)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">{headerSubtitle}</p>
          </div>
        </div>
        <CustomBadge
          color={BULK_UPLOAD_STATUS_MAP[row?.status] ?? "gray"}
          variant="light"
          size="sm"
        >
          {getDisplayValue(row?.status)}
        </CustomBadge>
      </div>

      <Divider color="gray.2" mb="md" />

      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-3">
        Payment Details
      </p>
      <Divider color="gray.2" mb={4} />

      <ReceiptFieldRow
        label="Transaction ID"
        value={getDisplayValue(row?.transactionId)}
      />
      <ReceiptFieldRow
        label={isMpesa ? "M-Pesa Reference" : "Gateway Reference"}
        value={getDisplayValue(row?.gatewayReferenceNumber)}
      />
      <ReceiptFieldRow
        label="Batch Entry Ref"
        value={getDisplayValue(row?.batchId)}
      />
      <ReceiptFieldRow label="Amount" value={formatKES(row?.amount)} />
      <ReceiptFieldRow
        label="Charges"
        value={formatKES(row?.charges)}
        valueClassName="text-gray-400"
      />
      <ReceiptFieldRow
        label="Total"
        value={formatKES(total)}
        labelClassName="font-semibold text-gray-900"
        valueClassName="font-semibold text-primary-700"
      />
      {isMpesa && (
        <ReceiptFieldRow
          label="Phone Number"
          value={getDisplayValue(row?.phoneNumber)}
        />
      )}
      {isBank && (
        <>
          <ReceiptFieldRow
            label="Account Number"
            value={getDisplayValue(row?.accountNumber)}
          />
          <ReceiptFieldRow
            label="Bank Code"
            value={getDisplayValue(row?.bankCode)}
          />
        </>
      )}
      <ReceiptFieldRow
        label="Channel"
        value={
          row?.paymentChannel ? (
            <CustomBadge color="primary" variant="light" size="sm">
              {CHANNEL_LABEL[row.paymentChannel] ?? row.paymentChannel}
            </CustomBadge>
          ) : (
            EMPTY_FIELD_PLACEHOLDER
          )
        }
      />
      <ReceiptFieldRow
        label="Date"
        value={formatDateTimeToLocale(row?.createdDateTime)}
      />
      <ReceiptFieldRow
        label="Posting Reference"
        value={getDisplayValue(row?.postingReference)}
      />
      <ReceiptFieldRow label="Remarks" value={getDisplayValue(row?.remarks)} />
    </>
  );
}

ReceiptContent.propTypes = {
  row: PropTypes.object,
};

export default function TransactionReceiptModal({
  recipientRow,
  opened,
  onClose,
  showPrint = false,
}) {
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: recipientRow?.transactionId
      ? `Receipt-${recipientRow.transactionId}`
      : "Transaction Receipt",
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={{ base: "100%", sm: 400 }}
      withCloseButton={false}
      zIndex={500}
      overlayProps={{ backgroundOpacity: 0.4, blur: 2 }}
      padding="lg"
      classNames={{ body: "!flex !flex-col !h-full" }}
    >
      <div className="shrink-0 flex items-center justify-between mb-5">
        <p className="text-base font-semibold text-gray-900">Payment Details</p>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <HiXMark size={18} />
        </button>
      </div>

      <Divider color="gray.2" mb="lg" className="shrink-0" />

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div ref={printRef} className="print:p-8">
          <ReceiptContent row={recipientRow} />
        </div>
      </div>

      {showPrint && (
        <div className="pt-4 shrink-0">
          <button
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            onClick={handlePrint}
          >
            <HiPrinter size={16} />
            Print Receipt
          </button>
        </div>
      )}
    </Drawer>
  );
}

TransactionReceiptModal.propTypes = {
  recipientRow: PropTypes.object,
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showPrint: PropTypes.bool,
};
